import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Flame } from "lucide-react";
import { QUIZZES, PRAISE } from "../data/quizzes";
import { useProgress } from "../context/ProgressContext";
import { Inline } from "./Inline";

function Question({ item, index }) {
  const { answers, record, flash } = useProgress();
  const answered = answers[item.id];
  const selected = answered ? answered.selected : null;
  const praise = React.useMemo(() => PRAISE[Math.floor(Math.random() * PRAISE.length)], []);

  const pick = (oi) => {
    if (selected !== null) return;
    record(item.id, oi, oi === item.a);
  };

  const letters = ["A", "B", "C", "D"];
  const showFlash = flash && flash.id === item.id;

  return (
    <div className="mb-10 last:mb-0 relative" data-testid={`quiz-question-${item.id}`}>
      <AnimatePresence>
        {showFlash && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.9 }}
            animate={{ opacity: 1, y: -6, scale: 1 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 -top-1 z-20 flex items-center gap-2 pointer-events-none"
            data-testid={`quiz-xp-${item.id}`}
          >
            {flash.correct && flash.combo > 1 && (
              <span className="flex items-center gap-1 font-mono-ui text-[11px] font-bold text-[#D26040]">
                <Flame size={13} /> {flash.combo}× streak
              </span>
            )}
            <span className={`font-mono-ui text-[12px] font-bold ${flash.correct ? "text-[#2F6F4F]" : "text-[#1C2430]/50"}`}>
              +{flash.delta} XP
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex gap-3 mb-4">
        <span className="font-mono-ui text-[11px] font-bold text-[#2F6F4F] pt-1 tabular-nums">Q{index + 1}</span>
        <p className="text-[15px] leading-relaxed text-[#1C2430] font-medium">{item.q}</p>
      </div>
      <div className="flex flex-col gap-2 pl-8">
        {item.o.map((opt, oi) => {
          const isSel = selected === oi;
          const isCorrect = oi === item.a;
          const revealed = selected !== null;
          let cls = "border-[var(--border-heavy)] bg-white hover:bg-black/[0.03]";
          if (revealed && isCorrect) cls = "border-[#2F6F4F] bg-[#2F6F4F]/8 text-[#2F6F4F]";
          else if (revealed && isSel && !isCorrect) cls = "border-[#D26040] bg-[#D26040]/8 text-[#D26040]";
          else if (revealed) cls = "border-[var(--border-light)] opacity-55";
          return (
            <motion.button
              key={oi}
              onClick={() => pick(oi)}
              whileTap={selected === null ? { scale: 0.985 } : {}}
              disabled={revealed}
              data-testid={`quiz-option-${item.id}-${oi}`}
              className={`text-left flex items-start gap-3 px-4 py-3 border transition-colors duration-200 ${cls} ${selected === null ? "cursor-pointer" : "cursor-default"}`}
            >
              <span className="font-mono-ui text-[11px] font-bold mt-0.5 w-4 shrink-0">{letters[oi]}</span>
              <span className="text-[14px] leading-snug flex-1">{opt}</span>
              {revealed && isCorrect && <Check size={16} className="shrink-0 mt-0.5" />}
              {revealed && isSel && !isCorrect && <X size={16} className="shrink-0 mt-0.5" />}
            </motion.button>
          );
        })}
      </div>
      <AnimatePresence>
        {selected !== null && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="ml-8 mt-3 overflow-hidden"
            data-testid={`quiz-explanation-${item.id}`}
          >
            <div className="border-l-2 border-[#1C2430]/30 pl-4 py-1 text-[13.5px] leading-relaxed text-[#1C2430]/75">
              <span className={`font-bold ${selected === item.a ? "text-[#2F6F4F]" : "text-[#D26040]"}`}>
                {selected === item.a ? `${praise} ` : `Not quite. The answer is ${letters[item.a]}. `}
              </span>
              <Inline text={item.e} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Quiz({ quizKey }) {
  const quiz = QUIZZES[quizKey];
  const { answers } = useProgress();
  if (!quiz) return null;
  const done = quiz.questions.filter((q) => answers[q.id]).length;
  const total = quiz.questions.length;
  const complete = done === total;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="my-12 border-2 border-[#1C2430] bg-white/70 backdrop-blur-sm p-7 md:p-9"
      data-testid={`quiz-${quizKey}`}
    >
      <div className="font-mono-ui text-[10px] font-bold uppercase tracking-[0.22em] text-[#2F6F4F] mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2">
          Practice · {quiz.label}
          {complete && <Check size={14} className="text-[#2F6F4F]" data-testid={`quiz-complete-${quizKey}`} />}
        </span>
        <span className="text-[#1C2430]/40">{done}/{total} · +{total * 10} XP</span>
      </div>
      <div className="h-[3px] bg-black/10 mb-6 overflow-hidden">
        <motion.div className="h-full bg-[#2F6F4F]" animate={{ width: `${(done / total) * 100}%` }} transition={{ duration: 0.5 }} />
      </div>
      {quiz.questions.map((item, i) => (
        <Question key={item.id} item={item} index={i} />
      ))}
    </motion.div>
  );
}
